import { useNavigate, useParams } from 'react-router';
import { useList, useOne } from '@refinedev/core';
import { Bento, BentoCell, HeroFigure } from '../../../components/Bento.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { humanizeEnum } from '../../../components/Form.js';

/**
 * One property, as its owner sees it.
 *
 * Same endpoints as the staff screen, minus every write. `canDelete` is not passed to
 * `DocumentList` because no portal actor has a write policy on `documents` — showing a
 * Delete button that the database refuses would be a lie told by the UI.
 *
 * An out-of-scope id renders "not found" rather than "not allowed", and that is the
 * correct answer rather than a vague one: `getProperty` throws NotFound for a missing
 * row and for someone else's row alike, because distinguishing them would confirm the
 * property exists to someone who cannot see it.
 */
interface PropertyRecord {
  id: string;
  name: string;
  propertyType: string;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    county: string | null;
  };
  yearBuilt: number | null;
  unitCount: number;
}

interface UnitRow {
  id: string;
  label: string;
  bedrooms: number;
  bathrooms: string;
  squareFeet: number | null;
  status: string;
  marketRent: string;
}

const unitColumns: Column<UnitRow>[] = [
  { key: 'label', header: 'Unit', render: (row) => <strong>{row.label}</strong> },
  {
    key: 'beds',
    header: 'Beds / baths',
    render: (row) => `${String(row.bedrooms)} / ${row.bathrooms}`,
  },
  {
    key: 'sqft',
    header: 'Sq ft',
    numeric: true,
    render: (row) => (row.squareFeet === null ? '—' : row.squareFeet.toLocaleString()),
  },
  { key: 'status', header: 'Status', render: (row) => humanizeEnum(row.status) },
  // Decimal string, rendered verbatim (invariant 2).
  { key: 'rent', header: 'Market rent', numeric: true, render: (row) => `$${row.marketRent}` },
];

export function OwnerPropertyShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useOne<PropertyRecord>({
    resource: 'properties',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: units, isLoading: unitsLoading } = useList<UnitRow>({
    resource: 'units',
    filters: [{ field: 'propertyId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const property = data?.data;

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!property) {
    return (
      <section className="stack">
        <p className="alert alert--warn">
          That property is not available under this account. If you have recently acquired or sold
          it, the ownership record covering today is what decides this.
        </p>
        <button type="button" className="button" onClick={() => void navigate('/portal/owner')}>
          ← Your portfolio
        </button>
      </section>
    );
  }

  const unitRows = units?.data ?? [];
  // Occupancy from the unit status the office maintains — not inferred from lease
  // rows, which would be a second definition of "occupied" that could disagree with
  // the console's.
  const occupied = unitRows.filter((row) => row.status === 'OCCUPIED').length;

  return (
    <section>
      <div className="row">
        <h1>{property.name}</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/portal/owner')}
        >
          ← Your portfolio
        </button>
      </div>

      <Bento>
        {/* Occupancy is the headline for a single property — a count, and the one
            number an owner scans for. Money stays on the portfolio page, per lease. */}
        <BentoCell span="md">
          <HeroFigure
            label="Occupied"
            value={unitsLoading ? '—' : `${occupied} of ${unitRows.length}`}
            caption={
              unitsLoading
                ? 'Loading units…'
                : occupied === unitRows.length
                  ? 'Every unit is occupied.'
                  : `${unitRows.length - occupied} not currently occupied.`
            }
          />
        </BentoCell>

        <BentoCell span="md" title="Property">
          <div className="row">
            <span className="pill">{humanizeEnum(property.propertyType)}</span>
            {property.yearBuilt ? (
              <span className="bento__sub">Built {property.yearBuilt}</span>
            ) : null}
          </div>
          <address className="bento__hint">
            {property.address.line1}
            {property.address.line2 ? <>, {property.address.line2}</> : null}
            <br />
            {property.address.city}, {property.address.state} {property.address.postalCode}
            {property.address.county ? <> · {property.address.county} County</> : null}
          </address>
        </BentoCell>

        <BentoCell span="full" title="Units">
          <DataTable
            columns={unitColumns}
            rows={unitRows}
            rowKey={(row) => row.id}
            isLoading={unitsLoading}
            emptyMessage="No units on record for this property."
          />
        </BentoCell>

        <BentoCell span="full" title="Documents">
          <p className="bento__hint">
            Files the office has released to owners. Anything not shared with you is not listed here
            — visibility is set per document, and the default is staff-only.
          </p>
          <DocumentList filter={{ propertyId: property.id }} />
        </BentoCell>
      </Bento>
    </section>
  );
}
